import { useEffect, useRef, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { authClient } from "../lib/auth";

const API_BASE = import.meta.env.VITE_API_URL ?? "/api";

type Stage = "loading" | "email" | "code" | "error" | "fetching" | "ready";

/** Rewrite relative URLs in presentation HTML to route through the API. */
function rewriteAssetUrls(html: string, queryParam: string): string {
  const slug = window.location.pathname.split("/presentations/")[1]?.split("/")[0] || "";
  const assetBase = `${API_BASE}/presentations/${slug}`;
  const qp = queryParam ? `?${queryParam}` : "";

  html = html.replace(
    /(src|data|href)="(?!https?:\/\/)(?!\/\/)((?!#)[^"]+)"/g,
    (_match: string, attr: string, path: string) => {
      if (path.startsWith("data:") || path.startsWith("#")) return _match;
      return `${attr}="${assetBase}/${path}${qp}"`;
    }
  );

  html = html.replace(
    /data-background-iframe="(?!https?:\/\/)([^"]+)"/g,
    (_match: string, path: string) =>
      `data-background-iframe="${assetBase}/${path}${qp}"`
  );

  // Reveal.js tries history.replaceState() which is blocked inside
  // about:srcdoc iframes.  Force hash:false so it doesn't touch the URL.
  html = html.replace(
    /Reveal\.initialize\(\s*\{/,
    "Reveal.initialize({ hash: false, history: false,"
  );

  return html;
}

export default function Presentation() {
  const { slug } = useParams<{ slug: string }>();
  const [searchParams] = useSearchParams();
  const inviteToken = searchParams.get("invite");
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [stage, setStage] = useState<Stage>("loading");
  const [srcdoc, setSrcdoc] = useState<string | null>(null);
  const [viewerEmail, setViewerEmail] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [sending, setSending] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [codeError, setCodeError] = useState<string | null>(null);
  const [signingIn, setSigningIn] = useState(false);

  async function tryLoad() {
    // If we have an invite token, try it first (no browser auth session needed)
    if (inviteToken) {
      setStage("fetching");
      const res = await fetch(
        `${API_BASE}/presentations/${slug}?invite=${encodeURIComponent(inviteToken)}`
      );
      if (res.ok) {
        let html = await res.text();
        html = rewriteAssetUrls(html, `invite=${encodeURIComponent(inviteToken)}`);
        setSrcdoc(html);
        setStage("ready");
        return;
      }
      // Invalid/expired invite — fall through to normal auth
    }

    const { data } = await authClient.auth.getSession();
    const token =
      data.session?.access_token ??
      sessionStorage.getItem("flow360_access_token") ??
      null;

    if (!token) {
      setStage("email");
      return;
    }

    setStage("fetching");

    const res = await fetch(`${API_BASE}/presentations/${slug}`, {
      headers: { Authorization: `Bearer ${token}` },
    });

    if (res.status === 401) {
      setStage("email");
      return;
    }
    if (res.status === 403) {
      setError("You don't have access to this presentation.");
      setStage("error");
      return;
    }
    if (!res.ok) {
      setError(`Failed to load presentation (${res.status}). Please try again.`);
      setStage("error");
      return;
    }

    let html = await res.text();
    const tokenParam = token ? `?token=${encodeURIComponent(token)}` : "";
    html = rewriteAssetUrls(html, tokenParam.slice(1)); // strip leading ?

    setSrcdoc(html);
    setViewerEmail(data.session?.user?.email ?? null);
    setStage("ready");
  }

  useEffect(() => {
    let cancelled = false;

    // Listen for sign-in events only. We intentionally ignore INITIAL_SESSION
    // (the direct tryLoad() below handles initial load) and TOKEN_REFRESHED /
    // USER_UPDATED (re-fetching mid-presentation would reload the iframe and
    // lose the user's slide position).
    const {
      data: { subscription },
    } = authClient.auth.onAuthStateChange((event, session) => {
      if (event === "SIGNED_IN" && session && !cancelled) {
        tryLoad();
      }
    });

    tryLoad().catch((e) => {
      if (!cancelled) {
        setError(String(e));
        setStage("error");
      }
    });

    return () => {
      cancelled = true;
      subscription.unsubscribe();
    };
  }, [slug]);

  async function handleGoogleSignIn() {
    setSigningIn(true);
    const redirectTo = `${window.location.origin}${import.meta.env.BASE_URL || "/"}auth/callback?next=${encodeURIComponent(`/presentations/${slug}`)}`;
    const { error: oauthError } = await authClient.auth.signInWithOAuth({
      provider: "google",
      options: { redirectTo },
    });
    if (oauthError) {
      setError(oauthError.message);
      setStage("error");
    }
    setSigningIn(false);
  }

  async function handleSendCode(e: React.FormEvent) {
    e.preventDefault();
    setSending(true);
    setCodeError(null);
    // Thread sends a scanner-safe 8-digit code, plus a short-lived magic link
    // for mail clients that do not prefetch links.
    const redirectTo = `${window.location.origin}${import.meta.env.BASE_URL || "/"}auth/callback?next=${encodeURIComponent(`/presentations/${slug}`)}`;
    const { error: otpError } = await authClient.auth.signInWithOtp({
      email,
      options: {
        emailRedirectTo: redirectTo,
        data: { presentation_slug: slug },
      },
    });
    setSending(false);
    if (otpError) {
      setError(otpError.message);
      setStage("error");
    } else {
      setCode("");
      setStage("code");
    }
  }

  async function handleVerifyCode(e: React.FormEvent) {
    e.preventDefault();
    setVerifying(true);
    setCodeError(null);
    const { error: verifyError } = await authClient.auth.verifyOtp({
      email,
      token: code.trim(),
      type: "email",
    });
    setVerifying(false);
    if (verifyError) {
      setCodeError(
        verifyError.message.toLowerCase().includes("expired")
          ? "That code has expired. Request a new one below."
          : "That code didn't work. Double-check and try again."
      );
      return;
    }
    // Session is now established. tryLoad() will run via onAuthStateChange
    // (SIGNED_IN event), but we also kick it off directly to shave a tick.
    tryLoad();
  }

  // ── Shared styles ──
  const container: React.CSSProperties = {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    height: "100vh",
    background: "#0a1a12",
    fontFamily: "'Inter', system-ui, sans-serif",
    padding: 32,
  };
  const inputStyle: React.CSSProperties = {
    width: "100%",
    padding: "10px 14px",
    fontSize: 15,
    borderRadius: 8,
    border: "1px solid rgba(0,100,60,0.3)",
    background: "#0d1a13",
    color: "#edf2ef",
    outline: "none",
    marginBottom: 12,
    boxSizing: "border-box",
  };
  const btnPrimary: React.CSSProperties = {
    width: "100%",
    padding: "10px 0",
    fontSize: 14,
    fontWeight: 600,
    borderRadius: 8,
    border: "none",
    background: "#00894F",
    color: "#fff",
    cursor: "pointer",
    transition: "background 0.2s",
  };
  const activityNoticeStyle: React.CSSProperties = {
    color: "#7f9189",
    fontSize: 12,
    lineHeight: 1.45,
    margin: "16px 0 0",
  };
  const renderActivityNotice = () => (
    <p style={activityNoticeStyle}>
      Access to this presentation is logged. We record page views,
      timestamps, sign-in or invite identity when available, and basic
      technical metadata for security, support, and engagement analytics. See
      the{" "}
      <Link to="/privacy" style={{ color: "#9ad4b8" }}>
        Thread privacy notice
      </Link>
      .
    </p>
  );

  if (stage === "loading") {
    return (
      <div style={container}>
        <p style={{ color: "#6db5dd", fontSize: 14, fontFamily: "monospace" }}>
          Loading…
        </p>
      </div>
    );
  }

  if (stage === "email") {
    return (
      <div style={container}>
        <div style={{ maxWidth: 400, width: "100%", textAlign: "center" }}>
          <h1
            style={{
              color: "#edf2ef",
              fontSize: 22,
              fontWeight: 600,
              marginBottom: 8,
            }}
          >
            Presentation Access
          </h1>
          <p
            style={{
              color: "#b8c8c0",
              fontSize: 14,
              marginBottom: 24,
              lineHeight: 1.5,
            }}
          >
            Sign in to view this presentation.
          </p>

          {/* Google SSO */}
          <button
            onClick={handleGoogleSignIn}
            disabled={signingIn}
            style={{
              ...btnPrimary,
              background: "#fff",
              color: "#333",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 10,
              marginBottom: 20,
              fontWeight: 500,
              cursor: signingIn ? "wait" : "pointer",
            }}
          >
            <svg width="18" height="18" viewBox="0 0 48 48">
              <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
              <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
              <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
              <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
            </svg>
            {signingIn ? "Redirecting…" : "Sign in with Google"}
          </button>

          {/* Divider */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 12,
              marginBottom: 20,
            }}
          >
            <div style={{ flex: 1, height: 1, background: "rgba(255,255,255,0.1)" }} />
            <span style={{ color: "#64748b", fontSize: 12 }}>or use email</span>
            <div style={{ flex: 1, height: 1, background: "rgba(255,255,255,0.1)" }} />
          </div>

          {/* Email 8-digit code */}
          <form onSubmit={handleSendCode}>
            <input
              type="email"
              required
              placeholder="you@company.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              style={inputStyle}
            />
            <button
              type="submit"
              disabled={sending}
              style={{
                ...btnPrimary,
                background: sending ? "#004D2E" : "#00894F",
                cursor: sending ? "wait" : "pointer",
              }}
            >
              {sending ? "Sending…" : "Email me an 8-digit code"}
            </button>
          </form>
          {renderActivityNotice()}
        </div>
      </div>
    );
  }

  if (stage === "code") {
    return (
      <div style={container}>
        <div style={{ maxWidth: 400, width: "100%", textAlign: "center" }}>
          <h1
            style={{
              color: "#edf2ef",
              fontSize: 22,
              fontWeight: 600,
              marginBottom: 8,
            }}
          >
            Check your email
          </h1>
          <p
            style={{
              color: "#b8c8c0",
              fontSize: 14,
              lineHeight: 1.5,
              marginBottom: 24,
            }}
          >
            We sent an 8-digit code to{" "}
            <strong style={{ color: "#DE833E" }}>{email}</strong>.
          </p>

          <form onSubmit={handleVerifyCode}>
            <input
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              autoComplete="one-time-code"
              required
              maxLength={8}
              placeholder="12345678"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 8))}
              autoFocus
              style={{
                ...inputStyle,
                fontFamily: "'SF Mono', Menlo, Consolas, monospace",
                fontSize: 22,
                textAlign: "center",
                letterSpacing: 6,
              }}
            />
            {codeError && (
              <p
                style={{
                  color: "#e74c3c",
                  fontSize: 13,
                  marginBottom: 12,
                  textAlign: "center",
                }}
              >
                {codeError}
              </p>
            )}
            <button
              type="submit"
              disabled={verifying || code.length !== 8}
              style={{
                ...btnPrimary,
                background: verifying || code.length !== 8 ? "#004D2E" : "#00894F",
                cursor: verifying ? "wait" : code.length === 8 ? "pointer" : "not-allowed",
                opacity: code.length === 8 ? 1 : 0.6,
              }}
            >
              {verifying ? "Verifying…" : "Verify and continue"}
            </button>
          </form>

          <button
            onClick={() => {
              setStage("email");
              setCodeError(null);
            }}
            style={{
              background: "none",
              border: "none",
              color: "#00894F",
              cursor: "pointer",
              fontSize: 13,
              textDecoration: "underline",
              marginTop: 16,
            }}
          >
            Use a different email
          </button>
          {renderActivityNotice()}
        </div>
      </div>
    );
  }

  if (stage === "fetching") {
    return (
      <div style={container}>
        <div style={{ textAlign: "center", maxWidth: 360, width: "100%" }}>
          <p
            style={{
              color: "#b8c8c0",
              fontSize: 14,
              marginBottom: 20,
              fontFamily: "monospace",
            }}
          >
            Loading presentation&hellip;
          </p>
          <div
            style={{
              width: "100%",
              height: 4,
              borderRadius: 2,
              background: "rgba(0,100,60,0.2)",
              overflow: "hidden",
            }}
          >
            <div
              style={{
                width: "40%",
                height: "100%",
                borderRadius: 2,
                background: "#00894F",
                animation: "slide 1.2s ease-in-out infinite",
              }}
            />
          </div>
          <style>{`@keyframes slide { 0% { margin-left: 0% } 50% { margin-left: 60% } 100% { margin-left: 0% } }`}</style>
        </div>
      </div>
    );
  }

  if (stage === "error") {
    return (
      <div style={container}>
        <p
          style={{
            color: "#e74c3c",
            fontSize: 14,
            textAlign: "center",
            maxWidth: 500,
            marginBottom: 16,
          }}
        >
          {error}
        </p>
        <button
          onClick={() => setStage("email")}
          style={{
            background: "none",
            border: "1px solid rgba(0,100,60,0.3)",
            color: "#00894F",
            cursor: "pointer",
            fontSize: 13,
            padding: "8px 16px",
            borderRadius: 6,
          }}
        >
          Try again
        </button>
      </div>
    );
  }

  return (
    <iframe
      ref={iframeRef}
      srcDoc={srcdoc!}
      sandbox="allow-scripts allow-same-origin allow-popups"
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100vw",
        height: "100vh",
        border: "none",
      }}
      title={`Presentation: ${slug}`}
      onLoad={() => {
        if (viewerEmail && iframeRef.current?.contentWindow) {
          iframeRef.current.contentWindow.postMessage(
            { type: "viewer-identity", email: viewerEmail },
            "*",
          );
        }
      }}
    />
  );
}
